import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { formGroup, formControl, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { AccordionBlockSettings } from './AccordionBlockSettings';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { Container } from 'reactstrap';
import { FormText } from '@core/components/form';
import { Card, CardBody, CardTitle, Collapse } from 'reactstrap';
import { useDidMount } from '@core/utils/hooks/react';
import { useBloc } from '@core/utils/bloc';
import { AccordionBlockBloc } from './AccordionBlockBloc';

export enum AccordionBlockVariation {
  accordion = 'accordion',
}

function defineAccordionForm() {
  return formGroup({
    heading: formControl<string>({
      initialValue: 'Heading',
    }),
    paragraph: formControl<string>({
      initialValue:
        'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
    }),
  });
}

function defineAccordionBlockForm() {
  return formGroup({
    accordion: formArray(() => defineAccordionForm()),
    advance: defineBlockAdvanceForm({ paddingBottom: 30, paddingTop: 30 }),
  });
}

export type AccordionForm = ReturnType<typeof defineAccordionForm>;
export type AccordionBlockForm = ReturnType<typeof defineAccordionBlockForm>;

export const AccordionBlock = memo((props: CourseBuilder.BlockProps<AccordionBlockVariation>) => {
  const { variation, preview, lessonId } = props;
  const form = useMemo(defineAccordionBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(AccordionBlockBloc, lessonId);
  const accordions = useObservable(form.controls.accordion.items$, []);
  const isEditable = props.editable;

  useConfigureBlock({
    props,
    form,
    contentComponent: AccordionBlockSettings,
    contentProps: { form },
  });

  useDidMount(() => {
    if (props.preview && !formValue.accordion.length) {
      form.patchValue({
        accordion: [
          {
            heading: 'Heading',
            paragraph:
              'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
          },
        ],
      });
    }
  });

  const removeAccordion = useCallback(
    (index: number) => {
      form.controls.accordion.removeAt(index);
    },
    [form],
  );

  return (
    <BlockContainer className={`block-container d-flex flex-fill ${variation}`} advanceForm={formValue.advance}>
      <Container>
        {accordions.map((controls, index) => {
          return (
            <AccordionItem
              form={controls}
              key={controls.uid}
              preview={!!props.preview}
              index={index}
              isEditable={!!props.editable}
              removeAccordion={removeAccordion}
            />
          );
        })}
        {isEditable && (
          <div className="block-statement">
            <a className="btn btn-outline-primary btn-sm mr-2" onClick={() => form.controls.accordion.push()}>
              Add Accordion
            </a>
          </div>
        )}
      </Container>
    </BlockContainer>
  );
});

interface AccordionItemProps {
  form: AccordionForm;
  preview: boolean;
  index: number;
  isEditable: boolean;
  removeAccordion: (val: number) => void;
}

const AccordionItem = memo((props: AccordionItemProps) => {
  const { form, preview, index, isEditable } = props;
  const [isOpen, setOpen] = useState(false);

  const removeAccordion = useCallback(
    (index: number) => {
      props.removeAccordion(index);
    },
    [props],
  );

  return (
    <Card>
      <CardBody>
        <CardTitle className="card-title_items">
          <div className="mb-3">
            <FormText control={form.controls.heading} editable={isEditable} />
          </div>
          <div className="action">
            <a className="ml-2" onClick={() => setOpen(!isOpen)}>
              <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'} font-size-22`}></i>
            </a>
            {isEditable && (
              <a className="btn btn-outline-danger btn-sm mr-2" onClick={() => removeAccordion(index)}>
                <i className="bx bx-x"></i>
              </a>
            )}
          </div>
        </CardTitle>
        <Collapse isOpen={isOpen}>
          <FormText control={form.controls.paragraph} editable={isEditable} />
        </Collapse>
      </CardBody>
    </Card>
  );
});
